import { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from 'antd';
import QuestionnaireItem from '../examinee/questionnaire-item';
import QuestionnaireHeader from '../examinee/questionnaire-header';
import QuestionnairePartBreak from '../examinee/questionnaire-part-break';
import styles from '../../style/examinee/questionnaire-test-page.module.css';
import { useQuestionnaireSession } from '../../hooks/useQuestionnaireSession';
import { takeOverSession } from '../../utils/take-over-session';

const ExaminerQuestionnaireTestPage = () => {
  const { sessionID } = useParams();
  const history = useHistory();
  const token = useSelector((state) => state.token);

  // A session a rater is filling in from an emailed invite is not this page's to
  // edit -- two people in one form overwrite each other. Find out whose session
  // this is before offering the form, and show a rater's view-only (PIN-324).
  // One the examiner took over to finish is theirs, but saves to it need the key
  // taking it over hands out (PIN-325).
  const [sessionCheck, setSessionCheck] = useState({ status: 'checking' });

  const {
    items,
    documents,
    responses,
    guesses,
    headerData,
    loading,
    submitting,
    allAnswered,
    hasRespondentName,
    respondentNameRequired,
    handleResponseChange,
    handleGuessChange,
    handleUpdateData,
    handleParentNameChange,
    handleSubmit,
    resumedRespondentName,
    saveError,
    submitError
  } = useQuestionnaireSession(sessionID, {
    respondentNameRequired: true,
    successMessage: 'Responses have been recorded.',
    raterKey: sessionCheck.raterKey
  });

  useEffect(() => {
    let cancelled = false;
    // Refused most often because the questionnaire was submitted meanwhile;
    // reloading would get the same answer, so this is not a failed check.
    const takeOver = () =>
      takeOverSession(sessionID, token).then(
        (raterKey) => {
          if (!cancelled) setSessionCheck({ status: 'own', raterKey });
        },
        (error) => {
          console.error('Could not take the questionnaire over:', error);
          if (!cancelled) setSessionCheck({ status: 'refused', message: error.message });
        }
      );

    axios
      .post(
        process.env.REACT_APP_API_URL + '/session/info',
        { sessionID },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .then((response) => {
        if (cancelled) return;
        if (response.data?.sentToRater)
          history.replace(`/examiner/questionnaire/${sessionID}/progress`);
        else if (response.data?.takenOver) return takeOver();
        else setSessionCheck({ status: 'own' });
      })
      .catch((error) => {
        console.error('Could not check the session:', error);
        const message = error.response?.data?.message || error.message;
        if (!cancelled) setSessionCheck({ status: 'failed', message });
      });
    return () => {
      cancelled = true;
    };
  }, [history, sessionID, token]);

  const onSubmitSuccess = () => {
    history.push('/examiner/sessions');
  };

  if (sessionCheck.status === 'refused') {
    return (
      <div className={styles.loading} data-testid="questionnaire-session-error">
        <p>This questionnaire could not be taken over ({sessionCheck.message}).</p>
        <Button onClick={() => history.push('/examiner/sessions')}>Back to Active Sessions</Button>
      </div>
    );
  }

  if (sessionCheck.status === 'failed') {
    return (
      <div className={styles.loading} data-testid="questionnaire-session-error">
        This session could not be opened ({sessionCheck.message}). Please reload the page.
      </div>
    );
  }

  if (loading || sessionCheck.status === 'checking') {
    return (
      <div className={styles.loading} data-testid="questionnaire-loading">
        Loading questionnaire...
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <QuestionnaireHeader
          headerData={headerData}
          onParentNameChange={handleParentNameChange}
          parentNameRequired={respondentNameRequired}
          resumedParentName={resumedRespondentName}
        />

        <div className={styles.itemList} data-testid="questionnaire-item-list">
          {items.map((item, i) => (
            <Fragment key={item._id}>
              <QuestionnairePartBreak
                item={item}
                previousItem={items[i - 1]}
                parts={headerData?.parts}
              />
              <QuestionnaireItem
                index={i + 1}
                item={item}
                document={documents[item._id]}
                responses={responses}
                guesses={guesses}
                allowGuess={headerData?.allowGuess === true}
                onResponseChange={handleResponseChange}
                onGuessChange={handleGuessChange}
                onUpdateData={handleUpdateData}
              />
            </Fragment>
          ))}
        </div>

        <div className={styles.footer}>
          {saveError && (
            <p className={styles.incompleteWarning} data-testid="questionnaire-save-error">
              Some answers could not be saved ({saveError}). They will be sent again when you
              submit.
            </p>
          )}
          {submitError && (
            <p className={styles.incompleteWarning} data-testid="questionnaire-submit-error">
              The responses could not be submitted: {submitError}
            </p>
          )}
          {headerData && !hasRespondentName && (
            <p className={styles.incompleteWarning} data-testid="questionnaire-warning-name">
              Please enter the rater name before submitting.
            </p>
          )}
          {!allAnswered && (
            <p className={styles.incompleteWarning} data-testid="questionnaire-warning-incomplete">
              There are one or more items that need to be completed before you can submit this form.
            </p>
          )}
          <Button
            type="primary"
            size="large"
            onClick={() => handleSubmit(onSubmitSuccess)}
            loading={submitting}
            disabled={!allAnswered || !hasRespondentName || submitting}
            data-testid="questionnaire-submit"
          >
            Submit Questionnaire
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExaminerQuestionnaireTestPage;
